import { List, Popover, Tag, Tooltip, Typography } from 'antd'
import { LibraTicketGroup } from 'features/libra/ticket/libraTicketsApi'
import { Group, X } from 'lucide-react'
import {
  Dispatch,
  memo,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const ID_UNGROUPED = 'ungrouped'

const StyledListItem = styled(List.Item)<{ $isSelected: boolean }>`
  cursor: pointer;
  padding-left: 0.5rem !important;
  min-width: 24rem;
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.background3 : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.background3 : theme.forecast.background2};
  }
`

const StyledTag = styled(Tag)`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
`

const { Title } = Typography

const TitleWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.forecast.text1};
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`

const ListHeader = memo(({ onClose }: { onClose: () => void }) => (
  <Title level={5}>
    <HeaderWrapper>
      <TitleWrapper>
        <Group size={16} />
        <span>Select ticket group</span>
      </TitleWrapper>
      <CloseButton onClick={onClose} title='Show all tickets'>
        <X size={16} />
      </CloseButton>
    </HeaderWrapper>
  </Title>
))

ListHeader.displayName = 'ListHeader'

export interface TicketGroupSelectProps {
  groups: ReadonlyArray<LibraTicketGroup>
  /**
   * Selected group ID:
   * - undefined = show all tickets
   * - null = show ungrouped tickets
   * - string = show tickets for specific group
   */
  selectedGroupId: string | null | undefined
  setSelectedGroupId: Dispatch<SetStateAction<string | null | undefined>>
  changeButtonIcon?: ReactNode
  showUngrouped?: boolean
}

export const TicketGroupSelect = memo(
  ({
    groups,
    selectedGroupId,
    setSelectedGroupId,
    changeButtonIcon,
    showUngrouped = true,
  }: TicketGroupSelectProps) => {
    const [open, setOpen] = useState(false)

    const handleGroupClick = useCallback(
      (groupId: string | null) => {
        // If clicking on the already selected group, show all tickets (undefined)
        // Otherwise, select the clicked group
        setSelectedGroupId((prev) => (prev === groupId ? undefined : groupId))
      },
      [setSelectedGroupId]
    )

    const listItems = useMemo(
      () => [
        ...groups,
        ...(showUngrouped
          ? [{ id: ID_UNGROUPED, title: 'Ungrouped', tags: {} }]
          : []),
      ],
      [groups, showUngrouped]
    )

    const handleClose = useCallback(() => {
      setSelectedGroupId(undefined)
    }, [setSelectedGroupId])

    const renderItem = useCallback(
      ({ id, title }: { id: string; title: string }) => (
        <StyledListItem
          $isSelected={
            id === ID_UNGROUPED
              ? selectedGroupId === null
              : id === selectedGroupId
          }
          onClick={() => handleGroupClick(id === ID_UNGROUPED ? null : id)}>
          {title}
        </StyledListItem>
      ),
      [selectedGroupId, handleGroupClick]
    )

    const content = useMemo(
      () => (
        <List
          header={<ListHeader onClose={handleClose} />}
          dataSource={listItems}
          renderItem={renderItem}
        />
      ),
      [listItems, renderItem, handleClose]
    )

    return (
      <Popover
        content={content}
        trigger='click'
        placement='right'
        open={open}
        onOpenChange={setOpen}>
        <Tooltip
          title='Click to change ticket group'
          open={open ? false : undefined}>
          <StyledTag color={CantileverTheme.colors.SeaGreen}>
            {changeButtonIcon ?? <Group size={14} />}
          </StyledTag>
        </Tooltip>
      </Popover>
    )
  }
)

TicketGroupSelect.displayName = 'TicketGroupSelect'
